import { z } from 'zod';

export const PERFORMANCE_REVIEW_STATUSES = ['draft', 'submitted', 'acknowledged'] as const;
export const performanceReviewStatusEnum = z.enum(PERFORMANCE_REVIEW_STATUSES);
export type PerformanceReviewStatus = z.infer<typeof performanceReviewStatusEnum>;

const kpiSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(200),
  description: z.string().trim().max(1000).optional().or(z.literal('')),
  target: z.string().trim().max(200).optional().or(z.literal('')),
  achieved: z.string().trim().max(200).optional().or(z.literal('')),
  weightage: z.coerce.number().min(0).max(100).default(10),
  rating: z.coerce.number().min(0).max(5).default(0),
  comments: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type KpiInput = z.infer<typeof kpiSchema>;

/**
 * A quarterly performance review — KPI-weighted rating, drafted by a
 * reviewer, submitted, then acknowledged by the employee. Ported from
 * Office_project's `PerformanceReview` model. `overallRating` is always
 * server-computed from `kpis` (weighted average) — never accepted from the
 * client, matching the source's `calcOverall`.
 */
export const createPerformanceReviewSchema = z.object({
  employeeId: z.string().min(1, 'employeeId is required'),
  quarter: z.coerce.number().int().min(1).max(4),
  year: z.coerce.number().int().min(2000).max(2100),
  kpis: z.array(kpiSchema).default([]),
  strengths: z.string().trim().max(2000).optional().or(z.literal('')),
  improvements: z.string().trim().max(2000).optional().or(z.literal('')),
  reviewerComments: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type CreatePerformanceReviewInput = z.infer<typeof createPerformanceReviewSchema>;

/** Matches the source's own field allow-list on update — `employeeId`/`quarter`/`year` are immutable after creation. */
export const updatePerformanceReviewSchema = z.object({
  kpis: z.array(kpiSchema).optional(),
  strengths: z.string().trim().max(2000).optional().or(z.literal('')),
  improvements: z.string().trim().max(2000).optional().or(z.literal('')),
  reviewerComments: z.string().trim().max(2000).optional().or(z.literal('')),
  employeeComments: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type UpdatePerformanceReviewInput = z.infer<typeof updatePerformanceReviewSchema>;

export const acknowledgeReviewSchema = z.object({
  employeeComments: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type AcknowledgeReviewInput = z.infer<typeof acknowledgeReviewSchema>;

export const performanceReviewListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  employeeId: z.string().optional(),
  year: z.coerce.number().int().optional(),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  status: performanceReviewStatusEnum.optional(),
});

export type PerformanceReviewListQuery = z.infer<typeof performanceReviewListQuerySchema>;

export const performanceStatsQuerySchema = z.object({
  year: z.coerce.number().int().optional(),
});

export type PerformanceStatsQuery = z.infer<typeof performanceStatsQuerySchema>;
